import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  Link,
} from '@mui/material';
import {
  CalendarMonth,
  Receipt,
  People,
  Assessment,
  Folder,
  Chat,
} from '@mui/icons-material';
import { COLORS } from '@elevair/shared';

const features = [
  {
    icon: <CalendarMonth fontSize="large" />,
    title: 'Smart Booking',
    description: 'Let clients book 24/7 with deposits, packages, and recurring sessions.',
  },
  {
    icon: <Receipt fontSize="large" />,
    title: 'Invoicing',
    description: 'Professional branded invoices with one-click payments via Stripe.',
  },
  {
    icon: <People fontSize="large" />,
    title: 'Client CRM',
    description: 'Full client profiles, notes, activity timeline, and testimonials.',
  },
  {
    icon: <Folder fontSize="large" />,
    title: 'Project Management',
    description: 'Tasks, Kanban boards, time tracking, and project collaboration.',
  },
  {
    icon: <Chat fontSize="large" />,
    title: 'Messaging',
    description: 'Built-in messaging with clients, file sharing, and email fallback.',
  },
  {
    icon: <Assessment fontSize="large" />,
    title: 'Analytics',
    description: 'Revenue, booking, and client metrics to grow with confidence.',
  },
];

export default function HomePage() {
  return (
    <Box>
      {/* Navigation */}
      <Box
        component="nav"
        sx={{
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
            color: COLORS.primary,
          }}
        >
          Elevair
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button component={RouterLink} to="/login" color="inherit">
            Login
          </Button>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            color="primary"
          >
            Start Free Trial
          </Button>
        </Stack>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          backgroundColor: COLORS.background,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Elevate Your Business with{' '}
            <Box component="span" sx={{ color: COLORS.primary }}>
              Elevair
            </Box>
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            paragraph
            sx={{ mb: 4 }}
          >
            The all-in-one platform for service professionals to manage clients,
            bookings, invoices, and grow their business with confidence.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Start Your Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open('https://youtu.be/J95XbTvmIhA', '_blank', 'noopener')}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: COLORS.secondary,
                color: COLORS.secondary,
              }}
            >
              Watch Demo
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No credit card required • 14-day free trial
          </Typography>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
        >
          Everything You Need to{' '}
          <Box component="span" sx={{ color: COLORS.primary }}>
            Thrive
          </Box>
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 6 }}
        >
          Powerful features designed specifically for service professionals
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ color: COLORS.primary, mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: COLORS.primary,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join hundreds of service professionals who've streamlined their
            business and increased their revenue.
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            sx={{
              px: 5,
              py: 1.5,
              backgroundColor: 'white',
              color: COLORS.primary,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Start Your Free Trial Today
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Georgia, serif',
            color: COLORS.primary,
            mb: 1,
          }}
        >
          Elevair
        </Typography>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Elevair. All rights reserved.
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
          <Link component={RouterLink} to="/terms" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Terms of Service
          </Link>
          <Link component={RouterLink} to="/privacy" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Privacy Policy
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
